import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { F0Box } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Check, Clock } from '@factorialco/f0-react/icons/app'
import {
  PurchaseRequestDetailFormsDocument,
  PurchaseRequestDetailFormsQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import FormContent from './components/FormContent'
import type { FormItem } from './components/formsTypes'

type PurchaseRequest = NonNullable<
  NonNullable<PurchaseRequestDetailFormsQuery['procurement']>['purchaseRequest']
>

const splitName = (fullName: string | null | undefined) => ({
  firstName: fullName?.split(' ')[0] ?? '',
  lastName: fullName?.split(' ').slice(1).join(' ') ?? '',
})

const mapPurchaseRequestToFormItems = (purchaseRequest: PurchaseRequest): FormItem[] => {
  const items: FormItem[] = []

  const membership = purchaseRequest.formsMembership
  if (membership?.id && membership.materializedForm?.form) {
    const requester = purchaseRequest.requesterEmployee
    const { firstName, lastName } = splitName(requester?.fullName)
    items.push({
      id: `initial-${membership.id}`,
      title:
        membership.materializedForm.formTitle ??
        i18n.t('procurement.purchase_requests.detail.forms.initial_form'),
      subtitle: i18n.t('procurement.purchase_requests.detail.forms.main_form'),
      formId: membership.materializedForm.form.id,
      resourceType: membership.materializedForm.form.resourceType,
      membershipId: membership.id,
      answers: membership.answers ?? [],
      status: membership.status === 'completed' ? 'completed' : 'pending',
      respondentFirstName: firstName,
      respondentLastName: lastName,
      respondentAvatarUrl: requester?.avatar?.url ?? null,
      completedAt: membership.createdAt ?? null,
    })
  }

  for (const task of purchaseRequest.completeFormTask ?? []) {
    const taskMembership = task.formsMembership
    if (taskMembership?.id && task.completeFormFormId) {
      const respondent = taskMembership.respondent?.employee
      const { firstName, lastName } = splitName(respondent?.fullName)
      const formTitle =
        taskMembership.materializedForm?.formTitle ??
        task.taskName ??
        i18n.t('procurement.purchase_requests.detail.forms.form')
      items.push({
        id: `task-${task.id}`,
        title: formTitle,
        subtitle: formTitle,
        formId: task.completeFormFormId,
        resourceType: 'Procurement::TaskForm',
        membershipId: taskMembership.id,
        answers: taskMembership.answers ?? [],
        status: 'completed',
        respondentFirstName: firstName,
        respondentLastName: lastName,
        respondentAvatarUrl: respondent?.avatar?.url ?? null,
        completedAt: task.updatedAt,
      })
    }
  }

  return items
}

const PurchaseRequestDetailFormsPage = () => {
  const { id } = useParams()
  const [openForm, setOpenForm] = useState<FormItem | null>(null)

  const source = useGraphqlDataCollectionSource({
    document: PurchaseRequestDetailFormsDocument,
    mapVariables: () => ({ id: String(id) }),
    mapData: (data) => {
      const pr = data.procurement?.purchaseRequest
      if (!pr) return []
      return mapPurchaseRequestToFormItems(pr)
    },
    mapPagination: (data) => {
      const pr = data.procurement?.purchaseRequest
      if (!pr) return { total: 0 }
      return { total: mapPurchaseRequestToFormItems(pr).length }
    },
    itemOnClick: (item) => () => {
      const formItem: FormItem = {
        id: String(item.id),
        title: String(item.title),
        subtitle: String(item.subtitle),
        formId: String(item.formId),
        resourceType: String(item.resourceType),
        membershipId: String(item.membershipId),
        answers: Array.isArray(item.answers) ? item.answers : [],
        status: item.status === 'completed' ? 'completed' : 'pending',
        respondentFirstName: String(item.respondentFirstName ?? ''),
        respondentLastName: String(item.respondentLastName ?? ''),
        respondentAvatarUrl: item.respondentAvatarUrl ? String(item.respondentAvatarUrl) : null,
        completedAt: item.completedAt ? String(item.completedAt) : null,
      }
      setOpenForm(formItem)
    },
    includePaginationVars: false,
  })

  return (
    <ResponsivePage.Body variant="fullscreen">
      <F0Box paddingX="sm">
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'list',
              options: {
                itemDefinition: (item: FormItem) => ({
                  title:
                    `${item.respondentFirstName} ${item.respondentLastName}`.trim() || item.title,
                  description: [item.subtitle],
                  avatar: {
                    type: 'person' as const,
                    firstName: item.respondentFirstName,
                    lastName: item.respondentLastName,
                    src: buildUrl(item.respondentAvatarUrl),
                  },
                }),
                fields: [
                  {
                    label: i18n.t('procurement.purchase_requests.detail.forms.status'),
                    render: (item: FormItem) => ({
                      type: 'status' as const,
                      value: {
                        status: item.status === 'completed' ? 'positive' : 'warning',
                        label:
                          item.status === 'completed'
                            ? i18n.t('procurement.purchase_requests.detail.forms.completed')
                            : i18n.t('procurement.purchase_requests.detail.forms.pending'),
                      },
                    }),
                  },
                  {
                    label: i18n.t('common.date.date'),
                    render: (item: FormItem) => {
                      if (!item.completedAt) return { type: 'text' as const, value: '' }
                      return {
                        type: 'tag' as const,
                        value: {
                          label: i18nDateToString(
                            stringToDate(item.completedAt, 'backend_datetime'),
                            'ui_short_with_time'
                          ),
                          icon: item.status === 'completed' ? Check : Clock,
                        },
                      }
                    },
                  },
                ],
              },
            },
          ]}
        />
      </F0Box>

      {openForm && (
        <FormContent
          key={openForm.id}
          formItem={openForm}
          isOpen
          onClose={() => setOpenForm(null)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default PurchaseRequestDetailFormsPage
